// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
interface String {
  /**+
   * @description
   * Each parameter from the string must be enclosed within '{' and  '}', must be alphanumeric and can have underscores.
   * When the values are passed as an object, is not necessary to include '{' and '}' enclosing each key.
   * How to use it:
   *    - '{name} is the name of the {species}'.format({ name: 'Roger', species: 'dog' }) // -> 'Roger is the name of the dog'
   *    - '{name} is the name of the {species}'.format('Roger', 'dog') // -> 'Roger is the name of the dog'
   */
  format(
    this: string,
    ...rest: Record<string, string | number>[] | string[]
  ): string

  /**
   * @description
   * Each parameter from the string must start with ':', must be alphanumeric and can have underscores.
   * When the values are passed as an object, is not necessary to include ":" at the start of each key.
   * How to use it
   *    const routeStr = '/team/:token_id/members/:member_id/transactions'
   *    - routeStr.replaceParams({
   *      'token_id': 123,
   *      'member_id': 'abc'
   *    }) // -> '/team/123/members/abc/transactions'
   */
  replaceParams(
    this: string,
    ...rest: Record<string, string | number>[]
  ): string

  /**
   * @description
   * Lets you add search params either pass a string as a search string or an object
   * How to use it
   *    - '/dashboard'.addUrlParams({
   *      'teamId': 123,
   *      'memberId': 'abc'
   *    }) // -> '/dashboard?teamId=123&memberId=abc'
   *
   *   - '/dashboard'.addUrlParams('teamId=123&memberId=abc') // -> '/dashboard?teamId=123&memberId=abc'
   */
  addUrlParams(
    this: string,
    ...rest: Record<string, string | number | boolean>[] | string[]
  ): string
}

const formatString = (
  limitA: string,
  limitB: string,
  str: string,
  args: Record<string, string | number>[] | string[],
  prototypeName: string
) => {
  if (args.length) {
    const t = typeof args[0]
    const regexp = new RegExp(`${limitA}[a-z0-9_]+${limitB}`, 'gi')
    const matches = [...str.matchAll(regexp)]
    if ('string' === t || 'number' === t) {
      if (args.length < matches.length) {
        console.error(`${prototypeName} some parameters are missing`)
        return str
      }
      matches.forEach(([key], index) => {
        str = str.replace(key, args[index] as string)
      })
    } else {
      const obj = args[0] as Record<string, string>
      const regexpLimit = new RegExp(`[${limitA}${limitB}]`, 'gi')
      if (Object.keys(obj).length < matches.length) {
        console.error(`${prototypeName} some parameters are missing`)
        return str
      }
      matches.forEach(([key]) => {
        const objectKey = key?.replaceAll(regexpLimit, '')
        if (objectKey in obj) {
          str = str.replace(key, obj[objectKey])
        } else {
          console.error(`${prototypeName} some keys are missmatching`)
        }
      })
    }
  }
  return str
}

const addUrlParams = (
  str: string,
  args: Record<string, string | number | boolean>[] | string[]
): string => {
  const urlParams = new URLSearchParams('')
  if (typeof args[0] === 'string') {
    const searchParams = new URLSearchParams(args[0])
    searchParams.forEach((value, key) => urlParams.append(key, value))
  } else {
    Object.entries(args[0]).forEach(([key, value]) => {
      urlParams.append(key, String(value))
    })
  }
  return `${str}?${urlParams.toString()}`
}

// eslint-disable-next-line no-extend-native
String.prototype.format =
  String.prototype.format ||
  function (...rest) {
    return formatString(
      '\\{',
      '\\}',
      this.toString(),
      rest,
      `String.prototype.format`
    )
  }

// eslint-disable-next-line no-extend-native
String.prototype.replaceParams =
  String.prototype.replaceParams ||
  function (...rest) {
    return formatString(
      ':',
      '',
      this.toString(),
      rest,
      `String.prototype.replaceParams`
    )
  }

// eslint-disable-next-line no-extend-native
String.prototype.addUrlParams =
  String.prototype.addUrlParams ||
  function (...rest) {
    return addUrlParams(this.toString(), rest)
  }

// eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
interface URLSearchParams {
  /**+
   * @description
   * It will take the existing url query params and convert them to object
   *
   * @returns Record<string, string>
   */
  toObject(this: URLSearchParams): Record<string, string>

  /**+
   * @description
   * It will use toObject and count
   *
   * @returns boolean
   */
  hasParams(this: URLSearchParams): boolean
}

// eslint-disable-next-line no-extend-native
URLSearchParams.prototype.toObject =
  URLSearchParams.prototype.toObject ||
  function () {
    const queryStringParams: Record<string, string> = {}
    const iterator = this.entries()
    let next = iterator.next()
    do {
      if (next.value) {
        const [key, value] = next.value
        queryStringParams[key] = value
      }
      next = iterator.next()
    } while (next.value)
    return queryStringParams
  }

// eslint-disable-next-line no-extend-native
URLSearchParams.prototype.hasParams =
  URLSearchParams.prototype.hasParams ||
  function () {
    return Object.keys(this.toObject()).length > 0
  }
